import styles from './ProductSearchList.module.scss';
import classNames from 'classnames';
import { ProductCard, Icon, Form, SearchField } from 'ui/components';
import { Pagination, PaginationProps } from 'ui/components/2-molecules/Navigation/Pagination';
import { Button, Heading } from 'ui/components/1-atoms';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import { ChangeEventHandler, KeyboardEventHandler, useContext, useEffect, useRef, useState } from 'react';
import { Container } from 'ui/components/4-habitats/Container';
import ChevronIcon from 'ui/icons/icon-chevron.svg';
import { PageSpinner } from 'ui/components/1-atoms/Media/PageSpinner';
import { Modal } from 'ui/components';
import { useForm } from 'react-hook-form';

export interface ProductSearchListProps {
	className?: string;
	heading: string;
	columns: number;
	numberOfProducts: number;
	products: Models.Product[];
	paginationData?: PaginationProps;
	isLoggedIn: boolean;
	isLoading?: boolean;
	searchWord: string;
	sortOptions: string[];
	handleSearchSubmit?: (query: string) => void;
	handleAddProductToCart: (product: Models.UpdateBasketProduct) => void;
	hasBp: boolean;
	sortingDefaultValue?: string;
	setSortingDefaultValue?: React.Dispatch<React.SetStateAction<string>>;
	handleChange?: ChangeEventHandler<HTMLInputElement>;
	handleKeyUp?: KeyboardEventHandler<HTMLInputElement>;
	placeholder?: string;
	state?: {
		required?: boolean;
		disabled?: boolean;
		hasError?: boolean;
		isHidden?: boolean;
	};
	searchContent?: {
		title: string;
		description: string;
		url: string;
		contentType: string;
		score: number;
	}[];
}

export const ProductSearchList: React.FC<ProductSearchListProps> = ({
	className,
	columns,
	numberOfProducts,
	products,
	paginationData,
	isLoggedIn,
	isLoading,
	searchWord = 'Polishing',
	sortOptions,
	handleSearchSubmit,
	handleAddProductToCart,
	hasBp,
	sortingDefaultValue,
	setSortingDefaultValue,
	placeholder,
	state: { required, disabled, hasError, isHidden } = {},
	handleKeyUp,
	searchContent,
}) => {
	// Gets column width for Product Card
	const getColumnWidth = (columns: number) => {
		if (columns > 4) columns = 4;
		if (!columns || columns === 1) columns = 2;
		const columnsWidth = Math.round(100 / columns);
		return JSON.stringify(columnsWidth) as Models.GridCellWidth;
	};
	const columnWidth = getColumnWidth(columns);
	const dictionary = useContext(DictionaryContext);
	// Defines image sizes for Product Card
	const imageSizes = '(max-width: 768px) 50vw, ' + columnWidth + 'vw';
	const [isOpen, setIsOpen] = useState(false);

	function handleChange(event) {
		const params = new URL(document.location as any).searchParams;
		const query = params.get('query');
		const page = params.get('page');

		history.pushState({}, '', `/search?query=${query}&page=${page}&sorting=${event.target.value}`);
	}

	const toggleList = () => {
		setIsOpen(!isOpen);
	};

	const [openToastNoti, setOpenToastNoti] = useState<boolean>(false);
	const handleCloseModal = () => setOpenToastNoti(false);
	const handleShowToastNotification = () => {
		setOpenToastNoti(!openToastNoti);
	};

	useEffect(() => {
		const params = new URL(document.location as any).searchParams;
		const sorting = params.get('sorting');
		if (sorting && sortOptions) {
			for (let i = 0; i < sortOptions.length; i++) {
				if (sorting === sortOptions[i]) {
					setSortingDefaultValue(sortOptions[i]);
					break;
				}
			}
		}
	});

	const dictionaryArticle = dictionary.getValue('Search.Articles', null, 'Articles');
	const dictionaryProduct = dictionary.getValue('Search.Products', null, 'Products');

	const [activeTab, setActiveTab] = useState<typeof dictionaryArticle | typeof dictionaryProduct>(dictionaryArticle);
	const buttonsRef = useRef<HTMLButtonElement[]>([]);
	const indicatorRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const index = activeTab === dictionaryArticle ? 0 : activeTab === dictionaryProduct ? 1 : 2;

		const currentButton = buttonsRef.current[index];
		const indicator = indicatorRef.current;

		if (currentButton && indicator) {
			const { offsetLeft, offsetWidth } = currentButton;
			indicator.style.transform = `translateX(${offsetLeft}px)`;
			indicator.style.width = `${offsetWidth}px`;
		}
	}, [activeTab]);

	type FormData = {
		query: string;
		headerSearch: string;
	};

	const {
		handleSubmit,
		register,
		formState: { errors },
		setFocus,
	} = useForm<FormData>();

	const onSubmit = async ({ headerSearch }: { headerSearch: string }) => {
		handleSearchSubmit?.(headerSearch);
	};
	return (
		<Container width="StandardWeb">
			<div className={classNames(styles.ProductSearchList)}>
				{isLoading ? (
					<PageSpinner size="large" />
				) : (
					// Show toast notification on Add Product to Cart
					openToastNoti && (
						<Modal
							modalType="basketToastNotification"
							title={dictionary.getValue('ShoppingCart.ConfirmAddedToCart', null, 'Item added to cart')}
							ariaClose={'close'}
							handleClose={handleCloseModal}
							openToastNoti={openToastNoti}
						></Modal>
					)
				)}
				<div className={classNames(styles.ProductSearchList_tabs, className)}>
					<div className={styles.ProductSearchList_searchWrapper}>
						<Heading headingLevel="h2" style="displaySm" className={classNames(styles.ProductSearchList_heading)}>
							{dictionary.getValue('Search.Heading', null, 'Results for')} {'"'}
							{searchWord}
							{'"'}
						</Heading>
						<Form id="product-searchbar" role="search" onSubmit={handleSubmit(onSubmit)}>
							<div className={styles.ProductSearchList_searchFieldWrapper}>
								<SearchField
									className={styles.ProductSearchList_searchField}
									placeholder={placeholder || 'Search'}
									name="headerSearch"
									state={errors.query ? { hasError: true, required: true } : { required: true }}
									register={register}
								/>
								<Button style="primaryWeb" type="submit" className={styles.ProductSearchList_searchButton}>
									{dictionary.getValue('Search', null, 'Search')}
								</Button>
							</div>
						</Form>
					</div>
					<div className={styles.ProductSearchList_tabsHeader}>
						<button
							ref={(el) => {
								buttonsRef.current[0] = el;
							}}
							onClick={() => setActiveTab(dictionaryArticle)}
							className={classNames(styles.ProductSearchList_tabButton, { [styles.active]: activeTab === dictionaryArticle })}
						>
							{dictionaryArticle} ({searchContent?.length})
						</button>

						<button
							ref={(el) => {
								buttonsRef.current[1] = el;
							}}
							onClick={() => setActiveTab(dictionaryProduct)}
							className={classNames(styles.ProductSearchList_tabButton, { [styles.active]: activeTab === dictionaryProduct })}
						>
							{dictionaryProduct} ({numberOfProducts != null ? `${numberOfProducts}` : ' '})
						</button>

						<div ref={indicatorRef} className={styles.ProductSearchList_indicator}></div>
					</div>

					<div className={styles.ProductSearchList_tabContent}>
						<div
							className={classNames(styles.ProductSearchList_tabPanel, {
								[styles.active]: activeTab === dictionaryArticle,
							})}
						>
							<div className={classNames(styles.ProductSearchList_libraryCards, className)}>
								{searchContent?.map((searchItems, index) => (
									<article key={index} className={styles.ProductSearchList_libraryCard}>
										<h3 className={styles.ProductSearchList_libraryCard_title}>{searchItems?.title}</h3>
										<p className={styles.ProductSearchList_libraryCard_description}>{searchItems?.description}</p>
										<a
											href={searchItems?.url}
											className={styles.ProductSearchList_libraryCard_link}
											target="_blank"
											rel="noopener noreferrer"
										>
											{searchItems?.url}
										</a>
									</article>
								))}
							</div>
						</div>
						<div className={classNames(styles.ProductSearchList_tabPanel, { [styles.active]: activeTab === dictionaryProduct })}>
							<Heading headingLevel="h5" style="xs" className={classNames(styles.ProductSearchList_amount)}>
								<div className={classNames(styles.ProductSearchListSorting)}>
									<span className={classNames(styles.ProductSearchListSortingTitle)}>
										{dictionary.getValue('Search.SortBy', null, 'Sort by:')}
									</span>
									<div className={styles.ProductSearchListSorting_wrapper}>
										<select
											className={classNames(styles.ProductSearchListSorting_select)}
											value={sortingDefaultValue}
											onChange={handleChange}
											onClick={toggleList}
											onBlur={toggleList}
										>
											{sortOptions?.map((option, index) => (
												<option className={styles.ProductSearchListSorting_option} value={option} key={index}>
													{dictionary.getValue('Search.' + option.replace(' ', ''), null, option)}{' '}
												</option>
											))}
										</select>
										<Icon
											size="xl"
											className={classNames(styles.ProductSorting_buttonIcon, {
												[styles.isOpen]: isOpen,
											})}
										>
											<ChevronIcon />
										</Icon>
									</div>
								</div>
							</Heading>
							<div className={classNames(styles.ProductSearchListItems, className)}>
								{products.map((product, index) => {
									if (index < numberOfProducts) {
										return (
											<ProductCard
												product={product as Models.Product}
												columnWidth={columnWidth}
												key={index}
												imageSizes={imageSizes}
												isLoggedIn={isLoggedIn}
												onAddToCart={handleAddProductToCart}
												showToastNotification={handleShowToastNotification}
												hasBp={hasBp}
											/>
										);
									}
								})}
							</div>
							<Pagination {...paginationData} />
						</div>
					</div>
				</div>
			</div>
		</Container>
	);
};
